import React from "react";
import {
  ShieldCheck,
  FileText,
  XCircle,
  Receipt,
  Truck,
  TrendingUp,
  Users,
  Lock,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";

// "السياسات النشطة" — A read-only, visually rich reference page that
// summarizes all the business rules currently enforced by the system.
// Anyone (admin, new hire, accountant) can open this page and see at a
// glance how invoices, debts, drivers, supervisors and customer
// creation behave — without having to read the source code.
//
// The policies listed here are the LIVE rules — if a policy changes in
// code, this page must be updated in the same commit.
const policy = (icon, color, title, lines, kind = "ok") => ({
  icon, color, title, lines, kind,
});

const POLICIES = [
  policy(FileText, "#15803d", "كل فاتورة = دين فوراً", [
    "أي فاتورة يصدرها الزبون من iPad/تطبيقه → تُحتسب دين بمجرد إنشائها.",
    "أي بيع مباشر يُصدره الأدمن → دين فوري بكامل المبلغ.",
    "أي فاتورة يُصدرها المشرف → دين فوري بكامل المبلغ.",
    "حتى لو الفاتورة بحالة «pending» (لم تُؤكَّد بعد) — تُحتسب دين.",
  ], "ok"),

  policy(XCircle, "#DC2626", "الفواتير المرفوضة لا تُحتسب دين", [
    "إذا رفضتَ فاتورة → تختفي تماماً من حساب الدين.",
    "المخزون يُعاد للقيمة الأصلية تلقائياً.",
    "لو ترفض بقصد إلغاء فاتورة خاطئة فقط — لا حاجة لسند قبض بعدها.",
  ], "warn"),

  policy(Receipt, "#0ea5e9", "الطريقة الوحيدة لخصم الدين: سند قبض", [
    "سند قبض هو الأداة الرسمية الوحيدة لتنزيل قيمة من رصيد الزبون.",
    "يمكن أن يحتوي مبلغ نقدي + خصم (كلاهما يخصمان من الدين).",
    "تعديل يدوي من الأدمن متاح كاستثناء طارئ — يُسجَّل في balance_adjustments.",
  ], "ok"),

  policy(Truck, "#7c3aed", "السائق لا يتدخل بالدين أبداً", [
    "تأكيد التوصيل = تأكيد توصيل فقط. لا يزيد دين ولا ينقصه.",
    "السائق لا يستلم نقد رسمياً — أي نقد يُسلَّمه أمانة للأدمن.",
    "الأدمن هو الذي يُصدر سند قبض بعد استلام النقد من السائق.",
  ], "ok"),

  policy(TrendingUp, "#15803d", "الديون تتراكم", [
    "فاتورة 250 + فاتورة 100 = دين 350.",
    "إذا الزبون دفع 100 (سند قبض) → يبقى دينه 250.",
    "حتى لو دفع كامل الدين، الفواتير القديمة تبقى مرئية في السجل.",
  ], "ok"),

  policy(Lock, "#92400E", "إضافة الزبائن صلاحية الأدمن فقط", [
    "المشرف لا يقدر يضيف زبون من داخل تطبيقه.",
    "البيع المباشر يرفض أي فاتورة لزبون غير مسجّل.",
    "تيلة «إضافة زبون جديد» باللوحة الإدارية = المصدر الوحيد.",
  ], "ok"),

  policy(AlertTriangle, "#EAB308", "خصم الفئة (A+ 150 / B+ 50)", [
    "زبائن فئة A+ يحصلون على 150 د.ع/قطعة كمكافأة.",
    "زبائن فئة B+ يحصلون على 50 د.ع/قطعة كمكافأة.",
    "أي منتج عليه علامة «🚫 استثناء من خصم/مكافأة الفئة» لا يُحسَب في المكافأة.",
  ], "info"),
];

export default function AdminPoliciesTab() {
  return (
    <div className="space-y-4" dir="rtl" data-testid="admin-policies-tab">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-bl from-[#0f172a] via-[#1E3A5F] to-[#15803d] text-white p-5 shadow-2xl">
        <div className="absolute -top-8 -left-8 w-40 h-40 bg-[#FBBF24]/20 rounded-full blur-3xl pointer-events-none" />
        <div className="relative flex items-center gap-3">
          <div className="w-14 h-14 bg-white/15 backdrop-blur-sm rounded-2xl flex items-center justify-center">
            <ShieldCheck className="w-7 h-7" />
          </div>
          <div>
            <h2 className="font-black text-xl leading-tight">السياسات النشطة</h2>
            <p className="text-[11px] opacity-80 leading-snug">
              ملخّص حيّ ومحدّث للقواعد التي يطبّقها النظام تلقائياً — على الفواتير، الديون، السائق، المشرف، والمنتجات. مرجع سريع للأدمن وأي موظف جديد.
            </p>
          </div>
        </div>
      </div>

      {/* Policy cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {POLICIES.map((p, i) => {
          const Icon = p.icon;
          const bg = p.kind === "warn" ? "bg-[#FEF3C7]" : p.kind === "info" ? "bg-[#F0F9FF]" : "bg-[#ECFDF5]";
          const border = p.kind === "warn" ? "border-[#F59E0B]/40" : p.kind === "info" ? "border-[#0ea5e9]/30" : "border-[#15803d]/30";
          return (
            <div
              key={i}
              data-testid={`policy-card-${i}`}
              className={`${bg} ${border} border-2 rounded-2xl p-4 hover:shadow-md transition-shadow`}
            >
              <div className="flex items-start gap-2.5 mb-2">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                  style={{ background: p.color }}
                >
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-black text-[#1E3A5F] text-sm leading-tight pt-1">{p.title}</h3>
              </div>
              <ul className="space-y-1.5 mt-3">
                {p.lines.map((line, j) => (
                  <li key={j} className="flex items-start gap-1.5 text-[11px] text-[#1E3A5F]/85 leading-relaxed">
                    <CheckCircle2 className="w-3 h-3 mt-0.5 shrink-0" style={{ color: p.color }} />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      {/* Footer note */}
      <div className="bg-[#F3F6F4] rounded-2xl p-3 text-[11px] text-[#5C6E64] text-center leading-relaxed">
        هذه السياسات مُطبَّقة في الكود الحالي. إذا غيّر فريق التطوير قاعدة، هذه الصفحة تُحدَّث في نفس النشرة (deploy). آخر مراجعة: <b>Feb 10, 2026</b>.
      </div>
    </div>
  );
}
